import { useDialog } from '@hooks/useDialog';
import { formatDuration } from '@utils/timeUtils';
import { FormattedMessage } from 'react-intl';
import { ImageVerificationResult } from 'src/types/sentinel';

import './ImageInfoDialog.css';

interface ImageInfoDialogProps {
	isOpen: boolean;
	onClose: () => void;
	data: ImageVerificationResult;
}

export default function ImageInfoDialog({ isOpen, onClose, data }: ImageInfoDialogProps) {
	const { dialogRef, handleBackdropClick } = useDialog(isOpen, onClose);

	if (!isOpen || !data) return null;

	const { predictedLabel, confidence, verifications, executionTime } = data;

	return (
		<dialog ref={dialogRef} id="image-info-dialog" className="sentinel-dialog" onClick={handleBackdropClick} onCancel={onClose}>
			<header>
				<h2>
					<span className="icon icon-image" />
					<span>
						<FormattedMessage id="sentinel.image-verification.title" />
					</span>
				</h2>
				<button className="dialog-close-btn icon icon-cross" onClick={onClose} aria-label="Close" />
			</header>
			<div className="dialog-content">
				<div className="info-summary">
					<div className="summary-item">
						<strong>
							<FormattedMessage id="sentinel.image-verification.predicted-label" />{' '}
						</strong>
						<span className="value">{predictedLabel}</span>
					</div>
					<div className="summary-item">
						<strong>
							<FormattedMessage id="sentinel.image-verification.confidence" />{' '}
						</strong>
						<span className="value">{(confidence * 100).toFixed(0)}%</span>
					</div>
					<div className="summary-item">
						<strong>
							<FormattedMessage id="sentinel.image-verification.processing-time" />{' '}
						</strong>
						<span className="value">{formatDuration(executionTime)}</span>
					</div>
				</div>
				<div className="gallery-section">
					<h3>
						<FormattedMessage id="sentinel.image-verification.all-images" /> ({verifications.length})
					</h3>
					<div className="images-grid">
						{verifications.map((item, index) => (
							<div key={`${item.url}-${index}`} className="grid-item">
								<div className={`image-container ${item.isAiGenerated ? 'ai-generated' : ''}`}>
									<img src={item.url} alt={`Analysis frame ${index + 1}`} loading="lazy" />
									<span className={`badge ${item.isAiGenerated ? 'badge-ai' : ''}`}>
										<small>
											<FormattedMessage
												id="sentinel.image-verification.artificial-score"
												values={{ confidence: item.confidence.toFixed(0) }}
											/>
										</small>
									</span>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</dialog>
	);
}
